import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DisputeTimeline from '../components/DisputeTimeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DisputeDetail = ({ user, onLogout }) => {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [dispute, setDispute] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Message input
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Resubmission
  const [showResubmitForm, setShowResubmitForm] = useState(false);
  const [resubmitNotes, setResubmitNotes] = useState('');
  const [resubmitImages, setResubmitImages] = useState([]);
  const [resubmitPreviews, setResubmitPreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [submittingResubmit, setSubmittingResubmit] = useState(false);
  // Resubmission - non-image deliverable files
  const [resubmitFiles, setResubmitFiles] = useState([]);
  const [uploadingResubmitFile, setUploadingResubmitFile] = useState(false);
  
  // Escalation
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalating, setEscalating] = useState(false);
  
  const isClient = user?.id === dispute?.client_id;
  const isProvider = user?.id === dispute?.provider_id;
  const isAdmin = user?.email === 'chitopargas@gmail.com' || user?.is_owner;

  useEffect(() => {
    fetchDispute();
    fetchMessages();
    
    // Poll for new messages every 10 seconds
    const interval = setInterval(() => {
      fetchMessages();
    }, 10000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disputeId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchDispute = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/disputes/${disputeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDispute(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load dispute');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/disputes/${disputeId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/disputes/${disputeId}/messages`, {
        message: newMessage,
        attachments: []
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (resubmitImages.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    setUploadingImages(true);
    setError('');

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each file must be less than 5MB');
        continue;
      }

      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/service-image`, uploadData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.url) {
          setResubmitImages(prev => [...prev, response.data.url]);
          
          const reader = new FileReader();
          reader.onloadend = () => {
            setResubmitPreviews(prev => [...prev, reader.result]);
          };
          reader.readAsDataURL(file);
        }
      } catch (error) {
        setError(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
      }
    }

    setUploadingImages(false);
  };

  const removeImage = (index) => {
    setResubmitImages(prev => prev.filter((_, i) => i !== index));
    setResubmitPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleResubmitFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (resubmitFiles.length >= 5) {
      setError('Maximum 5 files allowed');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File must be less than 20 MB');
      return;
    }
    setUploadingResubmitFile(true);
    setError('');
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API}/upload/file`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      if (resp.data?.url) {
        setResubmitFiles(prev => [...prev, {
          url: resp.data.url,
          original_name: resp.data.original_name,
          size_bytes: resp.data.size_bytes,
          content_type: resp.data.content_type,
        }]);
      }
    } catch (err) {
      setError(`Failed to upload file: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingResubmitFile(false);
      e.target.value = '';
    }
  };

  const removeResubmitFile = (index) => {
    setResubmitFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleResubmit = async () => {
    if (!resubmitNotes.trim()) {
      setError('Please provide notes about your resubmission');
      return;
    }

    setSubmittingResubmit(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/disputes/${disputeId}/resubmit`, {
        notes: resubmitNotes,
        proof_images: resubmitImages,
        proof_files: resubmitFiles,
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('Work resubmitted successfully!');
      setShowResubmitForm(false);
      setResubmitNotes('');
      setResubmitImages([]);
      setResubmitPreviews([]);
      fetchDispute();
      fetchMessages();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to resubmit');
    } finally {
      setSubmittingResubmit(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalateReason.trim()) {
      setError('Please provide a reason for escalation');
      return;
    }

    setEscalating(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/disputes/${disputeId}/escalate`, {
        reason: escalateReason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('Dispute escalated to admin for review');
      setShowEscalateModal(false);
      setEscalateReason('');
      fetchDispute();
      fetchMessages();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to escalate');
    } finally {
      setEscalating(false);
    }
  };

  const handleApproveResubmission = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/disputes/${disputeId}/approve-resubmission`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('Resubmission approved! Payment will be released.');
      fetchDispute();
      fetchMessages();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to approve resubmission');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      resubmitted: 'bg-purple-100 text-purple-800 border-purple-300',
      escalated: 'bg-red-100 text-red-800 border-red-300',
      resolved: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getMessageStyle = (msg) => {
    if (msg.message_type === 'system' || msg.message_type === 'escalation' || msg.message_type === 'resolution') {
      return 'bg-gray-100 text-gray-700 text-center text-sm italic mx-auto max-w-md';
    }
    if (msg.sender_id === user?.id) {
      return 'bg-orange-500 text-white ml-auto';
    }
    if (msg.sender_role === 'admin') {
      return 'bg-purple-500 text-white';
    }
    return 'bg-gray-200 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-4xl mx-auto py-12 px-4">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!dispute) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-4xl mx-auto py-12 px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Dispute Not Found</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-orange-500 hover:text-orange-600"
          >
            ← Back to Dashboard
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const isResolved = dispute.status === 'resolved' || dispute.status === 'closed';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Dispute Resolution</h1>
              <p className="text-gray-600">{dispute.service_title}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusBadge(dispute.status)}`}>
              {dispute.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
            <button onClick={() => setError('')} className="float-right">&times;</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {success}
            <button onClick={() => setSuccess('')} className="float-right">&times;</button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chat Area */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
              <h2 className="text-white font-bold">Dispute Discussion</h2>
              <p className="text-orange-100 text-sm">
                {isClient ? `With: ${dispute.provider_name}` : `With: ${dispute.client_name}`}
              </p>
            </div>

            {/* Messages */}
            <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`max-w-[80%] p-3 rounded-lg ${getMessageStyle(msg)}`}
                  data-testid={`message-${msg.id}`}
                >
                  {msg.message_type !== 'system' && msg.message_type !== 'escalation' && msg.message_type !== 'resolution' && (
                    <div className="text-xs opacity-75 mb-1">
                      {msg.sender_name} • {msg.sender_role}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">{msg.message}</p>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {msg.attachments.map((img, idx) => (
                        <img
                          key={`${img}-${idx}`}
                          src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                          alt={`Attachment ${idx + 1}`}
                          className="w-full h-16 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => window.open(img.startsWith('http') ? img : `${BACKEND_URL}${img}`, '_blank')}
                        />
                      ))}
                    </div>
                  )}
                  <div className="text-xs opacity-50 mt-1">
                    {new Date(msg.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            {!isResolved && (
              <form onSubmit={sendMessage} className="p-4 border-t">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={sendingMessage || !newMessage.trim()}
                    className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? '...' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Timeline */}
            <DisputeTimeline dispute={dispute} messages={messages} />

            {/* Dispute Info */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-gray-800 mb-4">Dispute Details</h3>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">Reason:</span>
                  <p className="font-medium">{dispute.reason}</p>
                </div>
                <div>
                  <span className="text-gray-500">Description:</span>
                  <p className="text-gray-700">{dispute.description}</p>
                </div>
                <div>
                  <span className="text-gray-500">Disputed Amount:</span>
                  <p className="font-bold text-lg text-orange-600">₱{dispute.disputed_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Resubmission Attempts:</span>
                  <p className="font-medium">{dispute.resubmission_count || 0} / 3</p>
                </div>
                {dispute.escalated_to_admin && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-red-700 font-semibold">⚠️ Escalated to Admin</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            {!isResolved && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="font-bold text-gray-800 mb-4">Actions</h3>
                
                {/* Provider Actions */}
                {isProvider && !dispute.escalated_to_admin && (
                  <div className="space-y-3">
                    <button
                      onClick={() => setShowResubmitForm(!showResubmitForm)}
                      disabled={dispute.resubmission_count >= 3}
                      className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="resubmit-btn"
                    >
                      {dispute.resubmission_count >= 3 ? 'Max Resubmissions Reached' : 'Resubmit Completed Work'}
                    </button>
                    
                    {showResubmitForm && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                        <textarea
                          value={resubmitNotes}
                          onChange={(e) => setResubmitNotes(e.target.value)}
                          placeholder="Explain what you've done to address the client's concerns..."
                          rows="3"
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="resubmit-notes"
                        />
                        
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Add Proof Images (optional)</label>
                          <div className="flex flex-wrap gap-2">
                            {resubmitPreviews.map((preview, index) => (
                              <div key={preview} className="relative">
                                <img src={preview} alt={`Preview ${index}`} className="w-16 h-16 object-cover rounded" />
                                <button
                                  onClick={() => removeImage(index)}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            {resubmitImages.length < 5 && (
                              <label className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-blue-500">
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleImageUpload}
                                  className="hidden"
                                  disabled={uploadingImages}
                                />
                                {uploadingImages ? '...' : '+'}
                              </label>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Add Proof Files (optional · PDF/DOC/ZIP/MP4/etc. · 20 MB each)</label>
                          <ul className="space-y-2 mb-2">
                            {resubmitFiles.map((f, idx) => (
                              <li
                                key={`${f.name}-${idx}`}
                                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-sm"
                                data-testid={`resubmit-file-${idx}`}
                              >
                                <span>📎</span>
                                <a
                                  href={f.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 truncate text-gray-800 hover:text-blue-600"
                                  title={f.original_name}
                                >
                                  {f.original_name || 'file'}
                                </a>
                                <span className="text-xs text-gray-400">
                                  {f.size_bytes != null
                                    ? f.size_bytes < 1024 * 1024
                                      ? `${(f.size_bytes / 1024).toFixed(1)} KB`
                                      : `${(f.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                                    : ''}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeResubmitFile(idx)}
                                  className="text-red-500 hover:text-red-700 text-xs"
                                  data-testid={`remove-resubmit-file-${idx}`}
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                          {resubmitFiles.length < 5 && (
                            <label
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded text-sm cursor-pointer hover:bg-gray-700 transition disabled:opacity-50"
                              data-testid="add-resubmit-file-btn"
                            >
                              <input
                                type="file"
                                onChange={handleResubmitFileUpload}
                                className="hidden"
                                disabled={uploadingResubmitFile}
                              />
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              {uploadingResubmitFile ? 'Uploading…' : 'Attach a file'}
                            </label>
                          )}
                        </div>

                        <button
                          onClick={handleResubmit}
                          disabled={submittingResubmit || !resubmitNotes.trim()}
                          className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          data-testid="submit-resubmit-btn"
                        >
                          {submittingResubmit ? 'Submitting...' : 'Submit Resubmission'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Actions */}
                {isClient && (
                  <div className="space-y-3">
                    {dispute.status === 'resubmitted' && (
                      <button
                        onClick={handleApproveResubmission}
                        className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                        data-testid="approve-resubmit-btn"
                      >
                        ✓ Approve & Release Payment
                      </button>
                    )}
                    
                    {!dispute.escalated_to_admin && (
                      <button
                        onClick={() => setShowEscalateModal(true)}
                        className="w-full py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        data-testid="escalate-btn"
                      >
                        Escalate to Admin
                      </button>
                    )}
                  </div>
                )}

                {dispute.escalated_to_admin && !isAdmin && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-yellow-800 text-sm">
                      This dispute has been escalated to admin. Please wait for their review and decision.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Resolution Info */}
            {isResolved && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="font-bold text-green-700 mb-4">✓ Dispute Resolved</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-500">Resolution:</span> <span className="font-medium">{dispute.resolution_type?.replace('_', ' ')}</span></p>
                  {dispute.resolution_notes && (
                    <p><span className="text-gray-500">Notes:</span> {dispute.resolution_notes}</p>
                  )}
                  {dispute.penalty_percentage && (
                    <p><span className="text-gray-500">Payment Deduction:</span> {dispute.penalty_percentage}%</p>
                  )}
                  {dispute.suspension_days && (
                    <p><span className="text-gray-500">Suspension:</span> {dispute.suspension_days} days</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Escalate Modal */}
      {showEscalateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-4">Escalate to Admin</h3>
            <p className="text-gray-600 mb-4">
              Escalating will send this dispute to admin for review. They will make a final decision which may include penalties for the provider.
            </p>
            <textarea
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              placeholder="Why are you escalating this dispute?"
              rows="3"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              data-testid="escalate-reason"
            />
            <div className="flex gap-3">
              <button
                onClick={handleEscalate}
                disabled={escalating || !escalateReason.trim()}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-escalate-btn"
              >
                {escalating ? 'Escalating...' : 'Confirm Escalation'}
              </button>
              <button
                onClick={() => setShowEscalateModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DisputeDetail;
